/**
 * QR-Code Tischbestellung - Admin-Seite
 * 
 * Der Kunde kann:
 * - QR-Codes für alle Tische generieren
 * - QR-Codes herunterladen/drucken
 * - Einstellungen selbst konfigurieren
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  QrCode, Download, Settings, RefreshCw, Save, Printer,
  Check, X, Table2, Smartphone, Eye
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function QRCodeManagement() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({
    enabled: true,
    allow_payment: false,
    require_name: false,
    show_prices: true,
    custom_message: 'Scannen Sie den QR-Code um zu bestellen',
    auto_print_kitchen: true
  });
  
  // QR Codes
  const [qrCodes, setQrCodes] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Preview
  const [previewQR, setPreviewQR] = useState(null);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    loadData();
  }, [sessionToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      const [settingsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/qr-order/settings`, { headers }),
        fetch(`${BACKEND_URL}/api/qr-order/stats`, { headers })
      ]);
      
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/qr-order/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Einstellungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const generateAllQRCodes = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/qr-order/generate-all`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQrCodes(data.qr_codes || []);
        toast.success(`${data.count} QR-Codes generiert!`);
      } else {
        toast.error('Fehler beim Generieren');
      }
    } catch (error) {
      toast.error('Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  const downloadQRCode = (qr) => {
    // Base64 zu Blob
    const byteCharacters = atob(qr.qr_code_base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });
    
    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${qr.table_name.replace(/\s+/g, '-')}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success(`QR-Code für ${qr.table_name} heruntergeladen`);
  };

  const printAllQRCodes = () => {
    const printWindow = window.open('', '_blank');
    
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR-Codes drucken</title>
        <style>
          body { font-family: Arial, sans-serif; }
          .qr-container { 
            display: inline-block; 
            text-align: center; 
            margin: 20px; 
            page-break-inside: avoid;
          }
          .qr-container img { width: 200px; height: 200px; }
          .table-name { font-size: 18px; font-weight: bold; margin-top: 10px; }
          @media print {
            .qr-container { margin: 10px; }
          }
        </style>
      </head>
      <body>
    `;
    
    qrCodes.forEach(qr => {
      html += `
        <div class="qr-container">
          <img src="data:image/png;base64,${qr.qr_code_base64}" />
          <div class="table-name">${qr.table_name}</div>
        </div>
      `;
    });
    
    html += '</body></html>';
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Header */}
      <header className="bg-[#111C32] border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <QrCode className="w-6 h-6 text-blue-400" />
              QR-Code Tischbestellung
            </h1>
            <p className="text-sm text-slate-400">
              Gäste bestellen selbst via QR-Code - kein Personal nötig
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge 
              variant={settings.enabled ? "default" : "secondary"}
              className={settings.enabled ? "bg-emerald-500" : "bg-slate-600"}
            >
              {settings.enabled ? "Aktiv" : "Deaktiviert"}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="qrcodes" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="qrcodes">
              <QrCode className="w-4 h-4 mr-2" />
              QR-Codes
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* QR CODES TAB */}
          <TabsContent value="qrcodes" className="space-y-6">
            
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-[#111C32] border-slate-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">QR-Bestellungen</p>
                        <p className="text-2xl font-bold text-white">{stats.total_qr_orders}</p>
                      </div>
                      <Smartphone className="w-8 h-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-[#111C32] border-slate-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">Umsatz via QR</p>
                        <p className="text-2xl font-bold text-emerald-400">€{stats.total_qr_revenue?.toFixed(2)}</p>
                      </div>
                      <Table2 className="w-8 h-8 text-emerald-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-[#111C32] border-slate-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400">QR-Codes generiert</p>
                        <p className="text-2xl font-bold text-white">{stats.qr_codes_generated}</p>
                      </div>
                      <QrCode className="w-8 h-8 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Generate Button */}
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">QR-Codes generieren</h3>
                    <p className="text-sm text-slate-400">
                      Erstellt QR-Codes für alle Ihre Tische
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={generateAllQRCodes}
                      disabled={generating}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {generating ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <QrCode className="w-4 h-4 mr-2" />
                      )}
                      QR-Codes generieren
                    </Button>
                    {qrCodes.length > 0 && (
                      <Button
                        variant="outline"
                        onClick={printAllQRCodes}
                        className="border-slate-600"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Alle drucken
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* QR Codes Grid */}
            {qrCodes.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {qrCodes.map((qr) => (
                  <Card key={qr.table_id} className="bg-[#111C32] border-slate-700">
                    <CardContent className="pt-6 text-center">
                      <img
                        src={`data:image/png;base64,${qr.qr_code_base64}`}
                        alt={qr.table_name}
                        className="w-32 h-32 mx-auto mb-3 rounded"
                      />
                      <p className="font-medium text-white mb-3">{qr.table_name}</p>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadQRCode(qr)}
                          className="border-slate-600 text-xs"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewQR(qr)}
                          className="border-slate-600 text-xs"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Vorschau
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {qrCodes.length === 0 && !generating && (
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="py-12 text-center">
                  <QrCode className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">
                    Klicken Sie auf "QR-Codes generieren" um QR-Codes für Ihre Tische zu erstellen
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-[#111C32] border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">QR-Bestellung Einstellungen</CardTitle>
                <CardDescription className="text-slate-400">
                  Konfigurieren Sie wie die QR-Bestellung funktioniert
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Enable */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-300">QR-Bestellung aktivieren</Label>
                    <p className="text-sm text-slate-500">Gäste können via QR-Code bestellen</p>
                  </div>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
                  />
                </div>

                <div className="border-t border-slate-700 pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-300">Preise anzeigen</Label>
                      <p className="text-sm text-slate-500">Preise im Menü anzeigen</p>
                    </div>
                    <Switch
                      checked={settings.show_prices}
                      onCheckedChange={(checked) => setSettings({...settings, show_prices: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-300">Name erforderlich</Label>
                      <p className="text-sm text-slate-500">Gast muss Namen eingeben</p>
                    </div>
                    <Switch
                      checked={settings.require_name}
                      onCheckedChange={(checked) => setSettings({...settings, require_name: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-300">Auto-Küchendruck</Label>
                      <p className="text-sm text-slate-500">Bestellung automatisch an Küche drucken</p>
                    </div>
                    <Switch
                      checked={settings.auto_print_kitchen}
                      onCheckedChange={(checked) => setSettings({...settings, auto_print_kitchen: checked})}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Begrüßungstext</Label>
                  <Textarea
                    value={settings.custom_message}
                    onChange={(e) => setSettings({...settings, custom_message: e.target.value})}
                    placeholder="Text der auf der Bestellseite angezeigt wird"
                    className="bg-slate-800 border-slate-600 text-white mt-2"
                    rows={2}
                  />
                </div>

                <Button onClick={saveSettings} disabled={saving} className="w-full">
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Einstellungen speichern
                </Button>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="bg-blue-500/10 border-blue-500/30">
              <CardContent className="pt-6">
                <h3 className="font-medium text-blue-300 mb-2">💡 So funktioniert's:</h3>
                <ol className="text-sm text-blue-200 space-y-1 list-decimal list-inside">
                  <li>QR-Codes für Ihre Tische generieren</li>
                  <li>QR-Codes ausdrucken und auf die Tische legen</li>
                  <li>Gäste scannen den Code mit ihrem Handy</li>
                  <li>Gäste bestellen direkt - Bestellung erscheint an Ihrer Kasse</li>
                </ol>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Preview Dialog */}
      <Dialog open={!!previewQR} onOpenChange={() => setPreviewQR(null)}>
        <DialogContent className="bg-[#111C32] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{previewQR?.table_name}</DialogTitle>
          </DialogHeader>
          {previewQR && (
            <div className="text-center">
              <img
                src={`data:image/png;base64,${previewQR.qr_code_base64}`}
                alt={previewQR.table_name}
                className="w-64 h-64 mx-auto mb-4"
              />
              <p className="text-sm text-slate-400 mb-4 break-all">{previewQR.order_url}</p>
              <Button onClick={() => downloadQRCode(previewQR)} className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Herunterladen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
